import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { dictionariesApi } from '@/api/dictionaries'
import { wordsApi, type WordWithTags } from '@/api/words'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { DictionaryForm } from '@/components/dictionaries/DictionaryForm'
import { useAuthStore } from '@/stores/authStore'
import type { Dictionary, UpdateDictionaryData } from '@/types'
import { ROUTES } from '@/utils/constants'
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  useMediaQuery,
  Skeleton,
  FormControlLabel,
  Radio,
  RadioGroup,
  Breadcrumbs,
  Menu,
  MenuItem,
  Autocomplete,
  TextField,
  InputAdornment,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ImportExport as ImportIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'

export function DictionaryDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  useMediaQuery(theme.breakpoints.down('sm'))
  const user = useAuthStore((s) => s.user)

  const [dictionary, setDictionary] = useState<Dictionary | null>(null)
  const [words, setWords] = useState<WordWithTags[]>([])
  const [dictionaryLoading, setDictionaryLoading] = useState(true)
  const [wordsLoading, setWordsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [wordsPagination, setWordsPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    per_page: 50,
  })
  const [selectedWordIds, setSelectedWordIds] = useState<number[]>([])

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [importStrategy, setImportStrategy] = useState<'skip' | 'update'>('skip')
  const [importResult, setImportResult] = useState<{
    imported_count: number
    skipped_count: number
    updated_count: number
  } | null>(null)
  const [attachWordsList, setAttachWordsList] = useState<WordWithTags[]>([])
  const [attachWordsLoading, setAttachWordsLoading] = useState(false)
  const [attachSelectedIds, setAttachSelectedIds] = useState<number[]>([])
  const [attachSearchInput, setAttachSearchInput] = useState('')
  const [attachAppliedSearch, setAttachAppliedSearch] = useState('')
  const [attachExact, setAttachExact] = useState(false)
  const [attachTagsFilter, setAttachTagsFilter] = useState<number[]>([])
  const [attachPagination, setAttachPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    per_page: 20,
  })
  const [attachTagsOptions, setAttachTagsOptions] = useState<Tag[]>([])
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [wordActionsAnchorEl, setWordActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [wordActionsItem, setWordActionsItem] = useState<WordWithTags | null>(null)

  const dictionaryId = id ? parseInt(id, 10) : null
  const canManage =
    user &&
    dictionary &&
    (user.id === dictionary.owner_id || user.roles?.some((r) => r.slug === 'admin'))

  const isOwner = !!(user && dictionary && user.id === dictionary.owner_id)

  const fetchDictionary = useCallback(() => {
    if (!dictionaryId) return
    setDictionaryLoading(true)
    dictionariesApi
      .getDictionary(dictionaryId)
      .then(setDictionary)
      .catch((err: Error) => setError(err.message))
      .finally(() => setDictionaryLoading(false))
  }, [dictionaryId])

  const fetchWords = useCallback(
    (page = 1) => {
      if (!dictionaryId) return
      setWordsLoading(true)
      const params = {
        page,
        per_page: wordsPagination.per_page,
      }
      dictionariesApi
        .getDictionaryWords(dictionaryId, params)
        .then((res) => {
          setWords(res.data)
          setWordsPagination({
            currentPage: res.current_page,
            lastPage: res.last_page,
            total: res.total,
            per_page: res.per_page,
          })
        })
        .catch((err: Error) => setError(err.message))
        .finally(() => setWordsLoading(false))
    },
    [dictionaryId, wordsPagination.per_page]
  )

  const fetchAttachWords = useCallback(
    (page = 1, perPage?: number) => {
      setAttachWordsLoading(true)
      const effectivePerPage = perPage ?? attachPagination.per_page
      wordsApi
        .getWords({
          page,
          per_page: effectivePerPage,
          search: attachAppliedSearch || undefined,
          exact: attachExact || undefined,
          tags: attachTagsFilter.length ? attachTagsFilter : undefined,
          is_imported: false,
        })
        .then((res) => {
          setAttachWordsList(res.data)
          setAttachPagination({
            currentPage: res.current_page ?? 1,
            lastPage: res.last_page ?? 1,
            total: res.total ?? res.data.length,
            per_page: res.per_page ?? effectivePerPage,
          })
        })
        .catch(() => {
          setAttachWordsList([])
        })
        .finally(() => setAttachWordsLoading(false))
    },
    [
      attachAppliedSearch,
      attachExact,
      attachPagination.per_page,
      attachTagsFilter,
    ]
  )

  useEffect(() => {
    if (dictionaryId) fetchDictionary()
  }, [dictionaryId, fetchDictionary])

  useEffect(() => {
    if (dictionaryId) fetchWords(1)
  }, [dictionaryId, fetchWords])

  useEffect(() => {
    if (!isAttachModalOpen) return
    const timer = window.setTimeout(() => setAttachAppliedSearch(attachSearchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [attachSearchInput, isAttachModalOpen])

  useEffect(() => {
    if (!isAttachModalOpen) return
    fetchAttachWords(1)
  }, [fetchAttachWords, isAttachModalOpen])

  const handlePageChange = (_: unknown, newPage: number) => {
    fetchWords(newPage + 1)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const perPage = parseInt(e.target.value, 10)
    setWordsPagination((p) => ({ ...p, per_page: perPage }))
    fetchWords(1)
  }

  const handleEditDictionary = async (data: UpdateDictionaryData) => {
    if (!dictionary) return
    await dictionariesApi.updateDictionary(dictionary.id, data)
    setIsEditModalOpen(false)
    fetchDictionary()
  }

  const handleDeleteDictionary = async () => {
    if (!dictionary) return
    if (!window.confirm(t('dictionaries.deleteConfirm'))) return
    try {
      await dictionariesApi.deleteDictionary(dictionary.id)
      navigate(ROUTES.DICTIONARIES)
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const handleDetachSelected = async () => {
    if (!dictionaryId || selectedWordIds.length === 0) return
    try {
      await dictionariesApi.detachWordsFromDictionary(dictionaryId, selectedWordIds)
      setSelectedWordIds([])
      fetchWords(wordsPagination.currentPage)
      fetchDictionary()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const handleDeleteWord = async (word: WordWithTags) => {
    if (!window.confirm(t('words.deleteConfirm'))) return
    try {
      await wordsApi.deleteWord(word.id)
      setSelectedWordIds((prev) => prev.filter((id) => id !== word.id))
      fetchWords(wordsPagination.currentPage)
      fetchDictionary()
    } catch (err) {
      setError((err as Error).message || t('words.deleteError'))
    } finally {
      setWordActionsAnchorEl(null)
      setWordActionsItem(null)
    }
  }

  const openAttachModal = () => {
    setIsAttachModalOpen(true)
    setAttachSelectedIds([])
    setAttachSearchInput('')
    setAttachAppliedSearch('')
    setAttachTagsFilter([])
    setAttachPagination((p) => ({ ...p, currentPage: 1 }))
    if (attachTagsOptions.length === 0) {
      tagsApi
        .getList({ per_page: 100 })
        .then((r) => setAttachTagsOptions(r.data))
        .catch(() => setAttachTagsOptions([]))
    }
    fetchAttachWords(1)
  }

  const handleAttachSubmit = async () => {
    if (!dictionaryId || attachSelectedIds.length === 0) return
    try {
      await dictionariesApi.attachWordsToDictionary(dictionaryId, attachSelectedIds)
      setIsAttachModalOpen(false)
      fetchWords(1)
      fetchDictionary()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const handleImportSubmit = async () => {
    if (!dictionaryId) return
    try {
      const result = await dictionariesApi.importWordsFromDictionary(dictionaryId, importStrategy)
      setImportResult({
        imported_count: result.imported_count,
        skipped_count: result.skipped_count,
        updated_count: result.updated_count,
      })
    } catch (err) {
      setError((err as Error).message)
      setIsImportModalOpen(false)
    }
  }

  const toggleWordSelection = (wordId: number) => {
    setSelectedWordIds((prev) =>
      prev.includes(wordId) ? prev.filter((id) => id !== wordId) : [...prev, wordId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedWordIds.length === words.length) {
      setSelectedWordIds([])
    } else {
      setSelectedWordIds(words.map((w) => w.id))
    }
  }

  const handleHeaderMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHeaderMenuAnchorEl(event.currentTarget)
  }

  const handleHeaderMenuClose = () => {
    setHeaderMenuAnchorEl(null)
  }

  const openWordActionsMenu = (event: React.MouseEvent<HTMLElement>, word: WordWithTags) => {
    setWordActionsAnchorEl(event.currentTarget)
    setWordActionsItem(word)
  }

  const closeWordActionsMenu = () => {
    setWordActionsAnchorEl(null)
    setWordActionsItem(null)
  }

  if (!dictionaryId) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Typography color="error">Invalid dictionary ID</Typography>
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <RouterLink
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </RouterLink>
          <RouterLink
            to={ROUTES.DICTIONARIES}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {t('dictionaries.title')}
          </RouterLink>
          <Typography color="text.primary">
            {dictionary?.name ?? ''}
          </Typography>
        </Breadcrumbs>

        {dictionaryLoading ? (
          <Skeleton variant="text" width={300} height={48} sx={{ mb: 2 }} />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              <IconButton onClick={() => navigate(ROUTES.DICTIONARIES)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
                {dictionary?.name ?? ''}
              </Typography>
              <PageHelpPopover pageKey="dictionaries" />
              {canManage && (
              <>
                <IconButton size="small" onClick={handleHeaderMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={headerMenuAnchorEl}
                  open={Boolean(headerMenuAnchorEl)}
                  onClose={handleHeaderMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => {
                      setIsEditModalOpen(true)
                      handleHeaderMenuClose()
                    }}
                  >
                    <EditIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('common.edit')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleHeaderMenuClose()
                      handleDeleteDictionary()
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('common.delete')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleHeaderMenuClose()
                      openAttachModal()
                    }}
                  >
                    <AddIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('dictionaries.detail.attachWords')}
                  </MenuItem>
                  {selectedWordIds.length > 0 && (
                    <MenuItem
                      onClick={() => {
                        handleHeaderMenuClose()
                        handleDetachSelected()
                      }}
                    >
                      <RemoveIcon sx={{ mr: 1 }} fontSize="small" />
                      {t('dictionaries.detail.detachSelected')}
                    </MenuItem>
                  )}
                  {!isOwner && (
                    <MenuItem
                      onClick={() => {
                        handleHeaderMenuClose()
                        setImportResult(null)
                        setIsImportModalOpen(true)
                      }}
                    >
                      <ImportIcon sx={{ mr: 1 }} fontSize="small" />
                      {t('dictionaries.detail.importWords')}
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {dictionary?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dictionary.description}
          </Typography>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('dictionaries.detail.wordsTitle')} ({dictionary?.words_count ?? 0})
        </Typography>

        {wordsLoading ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {canManage && <TableCell padding="checkbox" />}
                  <TableCell>{t('words.word')}</TableCell>
                  <TableCell>{t('words.translation')}</TableCell>
                  <TableCell>{t('words.filterPartOfSpeech')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    {canManage && <TableCell padding="checkbox"><Skeleton variant="circular" width={24} height={24} /></TableCell>}
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell align="right"><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : words.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('dictionaries.detail.noWords')}</Typography>
            {canManage && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={openAttachModal}
                sx={{ mt: 2 }}
              >
                {t('dictionaries.detail.attachWords')}
              </Button>
            )}
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {canManage && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedWordIds.length > 0 && selectedWordIds.length < words.length
                          }
                          checked={words.length > 0 && selectedWordIds.length === words.length}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                    )}
                    <TableCell>{t('words.word', 'Слово')}</TableCell>
                    <TableCell>{t('words.translation', 'Перевод')}</TableCell>
                    <TableCell>{t('words.partOfSpeech', 'Часть речи')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {words.map((w) => (
                    <TableRow key={w.id} hover>
                      {canManage && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedWordIds.includes(w.id)}
                            onChange={() => toggleWordSelection(w.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <RouterLink
                          to={`/words/${w.id}`}
                          style={{ textDecoration: 'none', fontWeight: 600 }}
                        >
                          {w.word ?? ''}
                        </RouterLink>
                      </TableCell>
                      <TableCell>{w.translation ?? '—'}</TableCell>
                      <TableCell>
                        {w.part_of_speech ?? '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => openWordActionsMenu(e, w)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, flexWrap: 'wrap' }}>
              <TablePagination
                component="div"
                count={wordsPagination.total}
                page={Math.max(0, wordsPagination.currentPage - 1)}
                onPageChange={handlePageChange}
                rowsPerPage={wordsPagination.per_page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[20, 50, 100]}
                labelRowsPerPage={t('dictionaries.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('dictionaries.of')} ${count !== -1 ? count : to}`
                }
              />
            </Box>
          </>
        )}

        <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('common.edit')}</DialogTitle>
          <DialogContent>
            {dictionary && (
              <DictionaryForm
                initialValues={dictionary}
                onSubmit={handleEditDictionary}
                onCancel={() => setIsEditModalOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={isAttachModalOpen} onClose={() => setIsAttachModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('dictionaries.detail.selectWordsToAttach')}</DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 1.5 }}>
            {attachWordsLoading ? (
              <Skeleton variant="rectangular" height={240} />
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder={t('common.search')}
                    value={attachSearchInput}
                    onChange={(e) => setAttachSearchInput(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 160, flex: 1 }}
                  />
                  <Tooltip title={t('common.searchExactTooltip')}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={attachExact}
                          onChange={(e) => setAttachExact(e.target.checked)}
                        />
                      }
                      label={t('common.searchExact')}
                    />
                  </Tooltip>
                  <Autocomplete
                    multiple
                    size="small"
                    options={attachTagsOptions}
                    getOptionLabel={(tag) => tag.name}
                    value={attachTagsOptions.filter((tag) => attachTagsFilter.includes(tag.id))}
                    onChange={(_, newValue) => setAttachTagsFilter(newValue.map((t) => t.id))}
                    renderInput={(params) => (
                      <TextField {...params} placeholder={t('tags.title')} />
                    )}
                    sx={{ minWidth: 200, maxWidth: 320 }}
                  />
                </Box>
                <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              attachSelectedIds.length > 0 &&
                              attachSelectedIds.length < attachWordsList.length
                            }
                            checked={
                              attachWordsList.length > 0 &&
                              attachSelectedIds.length === attachWordsList.length
                            }
                            onChange={() => {
                              if (attachSelectedIds.length === attachWordsList.length) {
                                setAttachSelectedIds([])
                              } else {
                                setAttachSelectedIds(attachWordsList.map((w) => w.id))
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{t('words.word', 'Слово')}</TableCell>
                        <TableCell>{t('words.translation', 'Перевод')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attachWordsList.map((w) => (
                        <TableRow key={w.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={attachSelectedIds.includes(w.id)}
                              onChange={(e) =>
                                setAttachSelectedIds((prev) =>
                                  e.target.checked
                                    ? [...prev, w.id]
                                    : prev.filter((id) => id !== w.id)
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>{w.word ?? ''}</TableCell>
                          <TableCell>{w.translation ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                      {attachWordsList.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography color="text.secondary">
                              {t('dictionaries.detail.noWords')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={attachPagination.total}
                    page={Math.max(0, attachPagination.currentPage - 1)}
                    onPageChange={(_, newPage) => fetchAttachWords(newPage + 1)}
                    rowsPerPage={attachPagination.per_page}
                    onRowsPerPageChange={(e) => {
                      const perPage = parseInt(e.target.value, 10)
                      setAttachPagination((p) => ({ ...p, per_page: perPage }))
                      fetchAttachWords(1, perPage)
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                    labelRowsPerPage={t('dictionaries.rows_per_page')}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}–${to} ${t('dictionaries.of')} ${count !== -1 ? count : to}`
                    }
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1.5 }}>
            <Button onClick={() => setIsAttachModalOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleAttachSubmit}
              disabled={attachSelectedIds.length === 0}
            >
              {t('dictionaries.detail.attach')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isImportModalOpen}
          onClose={() => {
            setIsImportModalOpen(false)
            setImportResult(null)
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('dictionaries.detail.importWords')}</DialogTitle>
          <DialogContent>
            {!importResult ? (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('dictionaries.detail.importStrategy')}
                </Typography>
                <RadioGroup
                  value={importStrategy}
                  onChange={(e) => setImportStrategy(e.target.value as 'skip' | 'update')}
                >
                  <FormControlLabel
                    value="skip"
                    control={<Radio size="small" />}
                    label={t('dictionaries.detail.importStrategySkip')}
                  />
                  <FormControlLabel
                    value="update"
                    control={<Radio size="small" />}
                    label={t('dictionaries.detail.importStrategyUpdate')}
                  />
                </RadioGroup>
              </Box>
            ) : (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('dictionaries.detail.importResult')}
                </Typography>
                <Typography>
                  {t('dictionaries.detail.importedCount')}: {importResult.imported_count}
                </Typography>
                <Typography>
                  {t('dictionaries.detail.skippedCount')}: {importResult.skipped_count}
                </Typography>
                <Typography>
                  {t('dictionaries.detail.updatedCount')}: {importResult.updated_count}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {!importResult ? (
              <>
                <Button onClick={() => setIsImportModalOpen(false)}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleImportSubmit}>
                  {t('dictionaries.detail.importWords')}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setIsImportModalOpen(false)
                  setImportResult(null)
                  fetchWords(1)
                  fetchDictionary()
                }}
              >
                {t('common.ok')}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>

      <Menu
        anchorEl={wordActionsAnchorEl}
        open={Boolean(wordActionsAnchorEl)}
        onClose={closeWordActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (wordActionsItem) {
              closeWordActionsMenu()
              navigate(`/words/${wordActionsItem.id}`)
            }
          }}
        >
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.view')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (wordActionsItem) {
              handleDeleteWord(wordActionsItem)
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          {t('common.delete')}
        </MenuItem>
      </Menu>
    </DashboardLayout>
  )
}
